import { useEffect, useMemo, useState } from "react"
import { FieldType, FieldValue, FormField, FormSeparator, ModalId, O, option, useUIActions, ValidationModal2 } from "../../../../../features/ui"
import { Budget, Customer, SaleOrder } from "../../../state/types"
import { Data2, DataTypeForm2, CreditAccountPaymentTypeId, useDataTypesHooks } from "../../../../../features/data-types"
import { useLocation, useNavigate } from "react-router-dom"
import { SellerField } from "../../seller-field/sellerField"
import { PaymentCategory, PaymentTypeField, useAdministrationState, usePaymentTypeLoaders, useCreditPaymentHooks, usePaymentTypeHooks } from "../../../../administration"
import { useActions } from "./actions"
import { CustomerField } from "../../customer/customer-field/customerField"
import { OperationItemsTable, OperationItemFormModal, ItemValidation, PriceBreakdown, useOperationHooks, usePricesHooks, usePriceOutlineHooks } from "../../../../operations"
import { TableName } from "../../../../../tables"
import { useManagerHooks } from "../../../../manager"
import { Product } from "../../../../products"
import { useCustomerLoaders } from "../../../loaders/customerLoaders"
import { isDefined, round } from "../../../../../utils/typeUtils"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { useSaleOperationHooks } from "../../../hooks/saleOperationHooks"
import { useCustomerTypeLoaders } from "../../../loaders/customerTypeLoaders"
import { DeliveryDataFields, DeliveryData } from "../../../../deliveries"
import { AddressQuickFormModal, useAddressBuilders } from "../../../../../features/address"

type Props = {
    sale?: SaleOrder
    budget?: Budget
    customer?: Customer
    keepPrices?: boolean
}

export const SaleForm = (props: Props) => {
    const { sale, budget, keepPrices } = props
    const createMode = !sale
    const saleOrBudget = sale || budget
    const defaultCustomer = props.customer

    const { currentEmployee } = useAdministrationState()

    const pricesHooks = usePricesHooks()
    const { pathname, state } = useLocation()

    // Crear desde presupuesto manteniendo precios: cada ítem conserva el precio, ajuste de lista, IVA y descuentos del presupuesto
    const keepBudgetPrices = !!budget && !!keepPrices

    const defaultItemsData = useMemo(() => (sale?.items || budget?.items || []).map(item => {
        if (keepBudgetPrices) {
            return {
                ...item,
                id: undefined,
                customerTypeYield: item.customerTypeYield ?? 0
            }
        }
        return {
            ...item,
            id: budget ? undefined : item.id,
            price: pricesHooks.product.basePrice(item.productId),
            customerTypeYield: undefined,
            vatTaxYield: undefined,
            ...(budget ? { discounts: [] } : {})
        }
    }), [sale, budget, keepBudgetPrices])

    const defaultDeliveryData = useMemo(() => ({
        method: sale?.deliveryData?.method,
        addressId: sale?.deliveryData?.addressId,
        deliveryDate: sale?.deliveryData?.deliveryDate
    }), [sale?.deliveryData])

    const [saleHasDeliveries, setSaleHasDeliveries] = useState<boolean>(!createMode)
    const [customer, setCustomer] = useState<O<Customer>>(defaultCustomer)
    const [customerTypeId, setCustomerTypeId] = useState<O<string>>(state?.customerTypeId || saleOrBudget?.customerTypeId || defaultCustomer?.customerTypeId)
    // Con precios del presupuesto, los totales usan el ajuste guardado en cada ítem y no la lista actual
    const itemsCustomerTypeId = keepBudgetPrices ? undefined : customerTypeId
    const [itemsData, setItemsData] = useState<Data2[]>(defaultItemsData)
    const [currentItem, setCurrentItem] = useState<Data2>()
    const [deliveryData, setDeliveryData] = useState<Partial<DeliveryData>>(defaultDeliveryData)
    const [paymentTypeId, setPaymentTypeId] = useState<O<string>>(state?.paymentTypeId || sale?.paymentTypeId)
    const [customerCredit, setCustomerCredit] = useState<O<number>>()
    const [latePayment, setLatePayment] = useState<O<boolean>>(!!sale?.latePayment)
    const [loading, setLoading] = useState<boolean>(false)
    const [addNotes, setAddNotes] = useState<boolean>(!!sale?.notes)
    const [notes, setNotes] = useState<O<string>>(sale?.notes)

    const { currentBranchId } = useManagerHooks()
    const { hasDeliveries } = useSaleOperationHooks()
    const { validateItems, getNegativeStockWarnings } = useOperationHooks()
    const { getPriceOutlineLive } = usePriceOutlineHooks()
    const { getCustomerDebtData, getOverdueDebtData } = useCreditPaymentHooks()
    const { formatPrice } = useDataTypesHooks()
    const { getPaymentTypeYield, isCreditAccount } = usePaymentTypeHooks()
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()

    const { buildAddress } = useAddressBuilders()

    const customerLoaders = useCustomerLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()

    const { submitCustomerDeliveryAddress, submitSale } = useActions()
    const { openModal } = useUIActions()

    const priceOutline = useMemo(() => {
        const paymentTypeYield = sale?.paymentTypeId === paymentTypeId ? sale?.paymentTypeYield : getPaymentTypeYield(paymentTypeId)
        return getPriceOutlineLive(itemsData, itemsCustomerTypeId, paymentTypeYield)
    }, [itemsData, itemsCustomerTypeId, paymentTypeId])

    const loadPriceAndDebt = async () => {
        setLoading(true)
        if (customer?.creditAccount && isCreditAccount(paymentTypeId)) {
            const debtData = await getCustomerDebtData(customer.id)
            // Al editar solo se descuenta lo que esta venta ya suma a la cuenta corriente (nada si se pagaba con otro medio)
            const thisSaleDebt = !createMode ? debtData.operationsData.find(operationData => operationData.operation.id === sale?.id)?.debt || 0 : 0
            const debtWithoutThisSale = debtData.debt - thisSaleDebt
            // El crédito a favor se suma al límite: puede llevar el tope por encima del crédito otorgado.
            setCustomerCredit(round(customer.creditAccount!.creditLimit - debtWithoutThisSale + (customer.creditInFavor ?? 0)))
        } else {
            setCustomerCredit(undefined)
        }
        setLoading(false)
    }

    useEffect(() => {
        sale && hasDeliveries(sale).then(setSaleHasDeliveries)
    }, [sale])

    useEffect(() => {
        loadPriceAndDebt()
    }, [customer, paymentTypeId, priceOutline])

    const itemMatches = (itemData: Data2, productId: string, lotId?: string) => itemData.productId === productId && itemData.lotId === lotId
    const filterProducts = (product: Product) => currentItem?.productId === product.id ||
        (pricesHooks.product.isForSale(product.id) && !itemsData.map(itemData => itemData.productId).includes(product.id))
    const openFormModal = () => openModal(ModalId.OPERATION_ITEM_FORM)

    const customerTypeOptions = useMemo(() => {
        const customerTypes = customerTypeLoaders().findAllCustomerTypes(createMode)
            .filter(customerType => !customerType.archived || customerType.id === customerTypeId)
        return customerTypes.map(customerType => option(customerType.id, customerType.name))
    }, [])

    const paymentTypes = useMemo(() => paymentTypeLoaders().findAllPaymentTypes(createMode)
        .filter(paymentType => paymentType.category !== PaymentCategory.CREDIT_ACCOUNT || customer?.creditAccount)
    , [customer])

    const onChangeCustomer = async (value?: FieldValue) => {
        const { loadCustomer } = customerLoaders()
        const newCustomer = value ? await loadCustomer(value as string) : undefined
        setCustomer(newCustomer)
        setCustomerTypeId(newCustomer?.customerTypeId)
        setPaymentTypeId(newCustomer?.creditAccount ? CreditAccountPaymentTypeId : undefined)
    }

    const onCreateCustomer = () => {
        const options = {
            state: {
                from: pathname,
                createMode,
                customerTypeId,
                paymentTypeId
            }
        }
        navigate(getRoutePath(RoutePath.CUSTOMERS, RoutePath.CREATE), options)
    }

    const onChangeCustomerType = (value?: FieldValue) => setCustomerTypeId(value as O<string>)

    const onCreateItem = () => {
        setCurrentItem(undefined)
        openFormModal()
    }

    const onEditItem = (productId: string, lotId?: string) => {
        setCurrentItem(itemsData.find(itemData => itemMatches(itemData, productId, lotId)))
        openFormModal()
    }

    const onRemoveItem = (productId: string, lotId?: string) => {
        const newItemsData = itemsData.filter(itemData => !itemMatches(itemData, productId, lotId))
        setItemsData(newItemsData)
    }

    const onChangePaymentType = (value?: FieldValue) => {
        const newPaymentTypeId = value as O<string>
        setLatePayment(false)
        setPaymentTypeId(newPaymentTypeId)
    }

    const onChangeLatePayment = (value?: FieldValue) => setLatePayment(value as O<boolean>)

    const onChangeAddNotes = (value?: FieldValue) => {
        setAddNotes(!!value)
        setNotes(undefined)
    }

    const onChangeNotes = (value?: FieldValue) => setNotes(value as O<string>)

    const fields: FormField[] = [
        {
            name: 'info-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Información" />
        },
        SellerField({
            defaultValue: sale?.sellerId || currentEmployee?.id,
            narrow: true,
            active: createMode
        }),
        CustomerField({
            value: customer?.id,
            disabled: saleHasDeliveries,
            showAddress: true,
            narrow: true,
            active: createMode,
            onChange: onChangeCustomer,
            onCreateNew: onCreateCustomer
        }),
        {
            name: 'customerTypeId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMER_TYPES,
            value: customerTypeId,
            label: 'Lista de Precios',
            options: customerTypeOptions,
            narrow: true,
            onChange: onChangeCustomerType
        },
        {
            name: 'saleItems',
            type: FieldType.TABLE,
            render: () => (
                <OperationItemsTable
                    itemsData={itemsData}
                    createMode
                    customerTypeId={itemsCustomerTypeId}
                    onCreateItem={onCreateItem}
                    onEditItem={onEditItem}
                    onRemoveItem={onRemoveItem}
                />
            )
        },
        {
            name: 'payment-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Pago" />
        },
        PaymentTypeField({
            value: paymentTypeId,
            paymentTypes,
            onChange: onChangePaymentType
        }),
        {
            name: 'priceBreakdown',
            type: FieldType.PRICE,
            label: 'Precio',
            render: () => <PriceBreakdown data={priceOutline} />
        },
        {
            name: 'addNotes',
            type: FieldType.BOOLEAN,
            label: 'Agregar Notas al Pedido',
            value: addNotes,
            onChange: onChangeAddNotes
        }
    ]
    if (!isCreditAccount(paymentTypeId)) {
        const latePaymentField = {
            name: 'latePayment',
            type: FieldType.BOOLEAN,
            label: 'Pago contra Entrega',
            value: latePayment,
            onChange: onChangeLatePayment
        }
        fields.splice(9, 0, latePaymentField)
    }
    fields.splice(5, 0, ...DeliveryDataFields({
        table: TableName.SALE_ORDERS,
        defaultData: deliveryData,
        customer,
        addressIdValue: deliveryData.addressId,
        branchId: currentBranchId!,
        onChange: setDeliveryData
    }))
    if (addNotes) {
        const notesField = {
            name: 'notes',
            type: FieldType.LONG_TEXT,
            label: 'Notas',
            value: notes,
            onChange: onChangeNotes
        }
        fields.push(notesField)
    }
    
    const validate = async (): Promise<string[]> => {
        const validations = createMode ?
            await validateItems(itemsData, [ItemValidation.NOT_EMPTY, ItemValidation.FOR_SALE, ItemValidation.DEFINED_LOT, ItemValidation.AVAILABLE_STOCK], currentBranchId!) :
            []
        if (validations.length === 0) {
            // Al editar no cuenta la deuda vencida de esta misma venta
            const overdueDebtData = await getOverdueDebtData(customer!.id)
            const overdueDebt = round(overdueDebtData.operationsData
                .filter(operationData => createMode || operationData.operation.id !== sale?.id)
                .reduce((sum, operationData) => sum + operationData.debt, 0))
            overdueDebt > 0 && validations.push(`El cliente ${customer!.name} tiene deudas vencidas por ${formatPrice(overdueDebt)}`)
        }
        if (validations.length === 0) {
            const notEnoughCredit = isDefined(customerCredit) && priceOutline.total > customerCredit!
            notEnoughCredit && validations.push(`El monto de la venta (${formatPrice(priceOutline.total)}) supera el límite de crédito disponible (${formatPrice(customerCredit)}) del cliente ${customer?.name}`)
        }
        return validations
    }

    const onSubmit = async (data: Data2) => {
        const finalData = {
            ...data,
            notes: notes,
            internalNotes: sale?.internalNotes || []
        }
        await submitSale(
            finalData,
            currentBranchId!,
            itemsData,
            deliveryData,
            latePayment,
            notes,
            createMode,
            keepBudgetPrices
        )
    }

    const onCancel = () => navigate(-1)

    const onSaveItem = (newData: Data2) => {
        const matches = (data_1: Data2, data_2: Data2) => itemMatches(data_1, data_2.productId! as string, data_2.lotId as O<string>)
        const newItemsData = currentItem ?
            itemsData.map(itemData => matches(itemData, currentItem) ? newData : itemData) :
            itemsData.find(itemData => matches(itemData, newData)) ? itemsData : [...itemsData, newData]
        setItemsData(newItemsData)
    }

    const onCreateCustomerAddress = async (data: Data2) => {
        const address = buildAddress(data)
        const savedCustomer = await submitCustomerDeliveryAddress(customer!, address)
        setCustomer(savedCustomer)
        setDeliveryData({
            ...deliveryData,
            addressId: address.id
        })
    }

    return (
        <>
            <DataTypeForm2
               formId="sale-form"
               fields={fields}
               createMode={createMode}
               disableSubmit={loading}
               onSubmit={onSubmit}
               onCancel={onCancel}
               validate={validate}
               warn={createMode ? () => getNegativeStockWarnings(itemsData, currentBranchId!) : undefined}
               warnTitle="Sin stock disponible"
            />
            <OperationItemFormModal
                defaultData={currentItem}
                createMode={createMode}
                customerTypeId={customerTypeId}
                withLots
                defaultPrice={pricesHooks.product.basePrice}
                filterProducts={filterProducts}
                onSubmit={onSaveItem}
            />
            <AddressQuickFormModal onSubmit={onCreateCustomerAddress} />
            <ValidationModal2 title="Validaciones" />
        </>
    )
}
